"use client";

import React, { useState, useEffect, useRef } from "react";
import { useCategories } from "../context/CategoriesContext";
import { useNavigate } from "react-router-dom";

const Cat = () => {
  const { categories, loading, error } = useCategories();
  const navigate = useNavigate();
  const categoriesScrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  // Gradient colors for cards (cycles if more categories than colors)
  const gradients = [
    "from-blue-400 to-purple-500",
    "from-pink-400 to-red-500",
    "from-yellow-400 to-orange-500",
    "from-green-400 to-teal-500",
    "from-indigo-400 to-fuchsia-500",
    "from-orange-400 to-red-500",
  ];

  const handleCategoryClick = (id) => {
    navigate(`/products#category-${id}`);
  };

  const handleViewAllClick = () => {
    navigate('/products#browse-home-decoration');
  };

  // Check scroll position to show/hide arrows
  const checkScrollPosition = () => {
    const slider = categoriesScrollRef.current;
    if (!slider) return;

    const { scrollLeft, scrollWidth, clientWidth } = slider;
    setShowLeftArrow(scrollLeft > 10);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  // Drag scroll functionality with enhanced states
  const handleMouseDown = (e) => {
    const slider = categoriesScrollRef.current;
    if (!slider) return;

    setIsDragging(true);
    const startX = e.pageX - slider.offsetLeft;
    const scrollLeft = slider.scrollLeft;

    const onMove = (e) => {
      const x = e.pageX - slider.offsetLeft;
      slider.scrollLeft = scrollLeft - (x - startX) * 2;
      checkScrollPosition();
    };

    const onUp = () => {
      setIsDragging(false);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // Touch events for mobile drag
  const handleTouchStart = (e) => {
    const slider = categoriesScrollRef.current;
    if (!slider) return;

    setIsDragging(true);
    const startX = e.touches[0].pageX - slider.offsetLeft;
    const scrollLeft = slider.scrollLeft;

    const onTouchMove = (e) => {
      const x = e.touches[0].pageX - slider.offsetLeft;
      slider.scrollLeft = scrollLeft - (x - startX) * 1.5;
      checkScrollPosition();
    };

    const onTouchEnd = () => {
      setIsDragging(false);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };

    document.addEventListener("touchmove", onTouchMove);
    document.addEventListener("touchend", onTouchEnd);
  };

  // Arrow click handlers
  const scrollLeft = () => {
    const slider = categoriesScrollRef.current;
    if (slider) {
      slider.scrollBy({ left: -200, behavior: 'smooth' });
      setTimeout(checkScrollPosition, 300);
    }
  };

  const scrollRight = () => {
    const slider = categoriesScrollRef.current;
    if (slider) {
      slider.scrollBy({ left: 200, behavior: 'smooth' });
      setTimeout(checkScrollPosition, 300);
    }
  };

  // Initialize scroll position check
  useEffect(() => {
    checkScrollPosition();
    const slider = categoriesScrollRef.current;
    if (slider) {
      slider.addEventListener('scroll', checkScrollPosition);
      return () => slider.removeEventListener('scroll', checkScrollPosition);
    }
  }, [categories]);

  return (
    <section
      className="overflow-hidden relative w-full bg-[#076461] h-[220px] md:h-[200px] sm:h-[180px] xs:h-[160px] border-y-2 border-[#FCBD01]/20"
      aria-label="Product categories ribbon"
    >
      {/* Decorative background patterns with large-scale animations */}
      <div className="absolute inset-0 opacity-15">
        <div className="absolute top-4 left-8 w-16 h-16 border-2 border-white rounded-full max-md:w-12 max-md:h-12 max-sm:w-8 max-sm:h-8 animate-orbital"></div>
        <div className="absolute top-8 right-12 w-12 h-12 border border-white rounded-full max-md:w-8 max-md:h-8 max-sm:w-6 max-sm:h-6 animate-diagonal-drift"></div>
        <div className="absolute bottom-6 left-1/4 w-8 h-8 border border-white rounded-full max-md:w-6 max-md:h-6 max-sm:w-4 max-sm:h-4 animate-pulse-gentle"></div>
        <div className="absolute bottom-4 right-1/3 w-20 h-20 border-2 border-white rounded-full max-md:w-14 max-md:h-14 max-sm:w-10 max-sm:h-10 animate-float-fast"></div>
        <div className="absolute top-1/2 left-12 w-6 h-6 bg-white rounded-full max-md:w-4 max-md:h-4 max-sm:w-3 max-sm:h-3 animate-wave-motion"></div>
        <div className="absolute top-1/3 right-8 w-4 h-4 bg-white rounded-full max-md:w-3 max-md:h-3 max-sm:w-2 max-sm:h-2 animate-float-slow"></div>
        <div className="absolute bottom-1/3 right-1/4 w-3 h-3 bg-white rounded-full max-sm:w-2 max-sm:h-2 animate-bounce-subtle"></div>

        {/* Geometric patterns with complex rotation animations */}
        <div className="absolute top-6 left-1/3 w-8 h-8 border border-white transform rotate-45 max-md:w-6 max-md:h-6 max-sm:w-4 max-sm:h-4 animate-rotate-slow"></div>
        <div className="absolute bottom-8 left-1/2 w-6 h-6 border border-white transform rotate-45 max-md:w-4 max-md:h-4 max-sm:w-3 max-sm:h-3 animate-rotate-reverse"></div>
        <div className="absolute top-1/2 right-1/4 w-10 h-10 border border-white transform rotate-45 max-md:w-6 max-md:h-6 max-sm:w-4 max-sm:h-4 animate-rotate-medium"></div>

        {/* Additional floating elements for more movement */}
        <div className="absolute top-16 left-1/2 w-5 h-5 bg-white/50 rounded-full animate-float-medium"></div>
        <div className="absolute bottom-16 right-1/2 w-7 h-7 border border-white/60 rounded-full animate-orbital"></div>
        <div className="absolute top-1/4 left-1/5 w-4 h-4 bg-white/40 transform rotate-45 animate-diagonal-drift"></div>
      </div>

      {/* Left edge decoration with large-scale staggered animations */}
      <div className="absolute left-0 top-0 h-full w-16 lg:w-16 md:w-12 sm:w-8 hidden md:flex flex-col justify-center items-center opacity-25">
        <div className="space-y-4 md:space-y-3 sm:space-y-2">
          <div className="w-8 h-8 md:w-6 md:h-6 sm:w-4 sm:h-4 border-2 border-[#FCBD01] rounded-full animate-orbital"></div>
          <div className="w-6 h-6 md:w-4 md:h-4 sm:w-3 sm:h-3 bg-[#FCBD01] rounded-full animate-float-slow"></div>
          <div className="w-4 h-4 md:w-3 md:h-3 sm:w-2 sm:h-2 border border-[#FCBD01] rounded-full animate-wave-motion"></div>
          <div className="w-6 h-6 md:w-4 md:h-4 sm:w-3 sm:h-3 border-2 border-[#FCBD01] transform rotate-45 animate-rotate-slow"></div>
          <div className="w-4 h-4 md:w-3 md:h-3 sm:w-2 sm:h-2 bg-[#FCBD01] transform rotate-45 animate-rotate-reverse"></div>
          <div className="w-5 h-5 md:w-3 md:h-3 sm:w-2 sm:h-2 border border-[#FCBD01] rounded-full animate-diagonal-drift"></div>
          <div className="w-3 h-3 md:w-2 md:h-2 sm:w-1 sm:h-1 bg-[#FCBD01] rounded-full animate-pulse-gentle"></div>
        </div>
      </div>

      {/* Right edge decoration with large-scale staggered animations */}
      <div className="absolute right-0 top-0 h-full w-16 lg:w-16 md:w-12 sm:w-8 hidden md:flex flex-col justify-center items-center opacity-25">
        <div className="space-y-4 md:space-y-3 sm:space-y-2">
          <div className="w-0 h-0 border-l-4 border-r-4 border-b-6 md:border-l-3 md:border-r-3 md:border-b-4 border-l-transparent border-r-transparent border-b-[#FCBD01] animate-float-fast"></div>
          <div className="w-6 h-6 md:w-4 md:h-4 sm:w-3 sm:h-3 bg-[#FCBD01] transform rotate-45 animate-rotate-medium"></div>
          <div className="w-0 h-0 border-l-3 border-r-3 border-b-5 md:border-l-2 md:border-r-2 md:border-b-3 border-l-transparent border-r-transparent border-b-[#FCBD01] animate-wave-motion"></div>
          <div className="w-7 h-7 md:w-5 md:h-5 sm:w-3 sm:h-3 border-2 border-[#FCBD01] rounded-full animate-orbital"></div>
          <div className="w-4 h-4 md:w-3 md:h-3 sm:w-2 sm:h-2 bg-[#FCBD01] rounded-full animate-diagonal-drift"></div>
          <div className="w-5 h-5 md:w-3 md:h-3 sm:w-2 sm:h-2 border border-[#FCBD01] transform rotate-45 animate-rotate-reverse"></div>
          <div className="w-3 h-3 md:w-2 md:h-2 sm:w-1 sm:h-1 border border-[#FCBD01] rounded-full animate-float-medium"></div>
        </div>
      </div>

      {/* Drag Scroll Indicators */}
      {/* Left Arrow */}
      <div className={`absolute left-4 md:left-16 top-1/2 transform -translate-y-1/2 z-20 transition-all duration-300 ${showLeftArrow ? 'opacity-80 translate-x-0' : 'opacity-0 -translate-x-4'}`}>
        <button
          onClick={scrollLeft}
          className="w-10 h-10 md:w-12 md:h-12 bg-white/90 hover:bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group backdrop-blur-sm border border-white/20"
          aria-label="Scroll left"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6 text-[#076461] transform group-hover:-translate-x-0.5 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>

      {/* Right Arrow */}
      <div className={`absolute right-4 md:right-16 top-1/2 transform -translate-y-1/2 z-20 transition-all duration-300 ${showRightArrow ? 'opacity-80 translate-x-0' : 'opacity-0 translate-x-4'}`}>
        <button
          onClick={scrollRight}
          className="w-10 h-10 md:w-12 md:h-12 bg-white/90 hover:bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group backdrop-blur-sm border border-white/20"
          aria-label="Scroll right"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6 text-[#076461] transform group-hover:translate-x-0.5 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Gradient Overlays for Scroll Indication */}
      <div className={`absolute left-0 top-0 h-full w-20 md:w-32 bg-gradient-to-r from-[#076461] to-transparent z-10 pointer-events-none transition-opacity duration-300 ${showLeftArrow ? 'opacity-60' : 'opacity-0'}`}></div>
      <div className={`absolute right-0 top-0 h-full w-20 md:w-32 bg-gradient-to-l from-[#076461] to-transparent z-10 pointer-events-none transition-opacity duration-300 ${showRightArrow ? 'opacity-60' : 'opacity-0'}`}></div>

      {/* Drag Hint Text */}
      {/* <div className="absolute top-4 left-1/2 transform -translate-x-1/2">
        <div className={`flex items-center gap-2 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-xs md:text-sm transition-all duration-300 ${isDragging ? 'opacity-100 scale-105' : 'opacity-70 hover:opacity-100'}`}>
          <svg className="w-4 h-4 animate-pulse-gentle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
          </svg>
          <span className="hidden sm:inline">Drag to scroll</span>
          <span className="sm:hidden">Drag</span>
          <svg className="w-4 h-4 animate-pulse-gentle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </div>
      </div> */}

      {/* Main categories ribbon with drag scroll */}
      <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-6xl px-8 lg:px-8 md:px-6 sm:px-4 xs:px-2">
        <div
          ref={categoriesScrollRef}
          className={`flex items-center justify-start sm:justify-center gap-8 md:gap-6 sm:gap-4 overflow-x-auto scrollbar-hide py-4 select-none transition-all duration-300 ${
            isDragging
              ? 'cursor-grabbing scale-[0.98]'
              : 'cursor-grab hover:cursor-grab'
          }`}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onSelectStart={(e) => e.preventDefault()}
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            userSelect: "none",
            WebkitUserSelect: "none",
            MozUserSelect: "none",
          }}
        >
          {/* Loading & error */}
          {loading && <p className="text-white flex-shrink-0">Loading...</p>}
          {error && <p className="text-white flex-shrink-0">{error}</p>}

          {/* Dynamic category cards */}
          {categories.map((cat, i) => (
            <article
              key={cat.id}
              className="group cursor-pointer transition-all duration-300 hover:scale-105 flex-shrink-0 select-none"
              onClick={() => !isDragging && handleCategoryClick(cat.id)}
              onSelectStart={(e) => e.preventDefault()}
            >
              <div className="relative w-32 h-32 lg:w-32 lg:h-32 md:w-28 md:h-28 sm:w-20 sm:h-20 xs:w-16 xs:h-16">
                {/* Gradient background */}
                <div
                  className={`absolute inset-0 rounded-full shadow-lg group-hover:shadow-xl transition-all duration-300 bg-gradient-to-br ${gradients[i % gradients.length]}`}
                ></div>

                {/* Inner white circle */}
                <div className="absolute inset-1 bg-white/95 rounded-full"></div>

                {/* Content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
                  <img
                    src={cat.image || "https://via.placeholder.com/48"}
                    alt={cat.title}
                    className="w-12 h-12 lg:w-12 lg:h-12 md:w-10 md:h-10 sm:w-6 sm:h-6 xs:w-5 xs:h-5 object-contain mb-2 sm:mb-1 xs:mb-0 pointer-events-none"
                    draggable={false}
                  />
                  <h3 className="text-xs lg:text-xs md:text-[10px] sm:text-[8px] xs:text-[7px] font-bold text-gray-700 text-center leading-tight pointer-events-none">
                    {cat.title.split(" ").map((word, j) => (
                      <span key={j}>
                        {word}
                        <br />
                      </span>
                    ))}
                  </h3>
                </div>
              </div>
            </article>
          ))}

          {/* View All Button */}
          <div
            className="group cursor-pointer flex-shrink-0 select-none"
            onClick={() => !isDragging && handleViewAllClick()}
            onSelectStart={(e) => e.preventDefault()}
          >
            <div className="relative w-32 h-32 lg:w-32 lg:h-32 md:w-28 md:h-28 sm:w-20 sm:h-20 xs:w-16 xs:h-16">
              <div
                className="absolute inset-0 animate-spin pointer-events-none"
                style={{ animationDuration: "8s" }}
              >
                <svg className="w-full h-full" viewBox="0 0 128 128">
                  <defs>
                    <path
                      id="circle"
                      d="M 64, 64 m -35, 0 a 35,35 0 1,1 70,0 a 35,35 0 1,1 -70,0"
                    />
                  </defs>
                  <text
                    className="text-[10px] lg:text-[10px] md:text-[9px] sm:text-[7px] xs:text-[6px] tracking-widest font-medium fill-white"
                    style={{ fontSize: "12px" }}
                  >
                    <textPath href="#circle" startOffset="0%">
                      •VIEW ALL • VIEW ALL • VIEW ALL • VIEW ALL •
                    </textPath>
                  </text>
                </svg>
              </div>

              <button
                className="absolute inset-0 m-auto w-12 h-12 lg:w-12 lg:h-12 md:w-10 md:h-10 sm:w-6 sm:h-6 xs:w-5 xs:h-5 bg-gradient-to-br from-[#FCBD01] to-[#e6a800] rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-4 focus:ring-[#FCBD01]/30 flex items-center justify-center group"
                aria-label="View all product categories"
              >
                <div
                  className="animate-spin pointer-events-none"
                  style={{ animationDuration: "8s" }}
                >
                  <svg
                    className="w-5 h-5 lg:w-5 lg:h-5 md:w-4 md:h-4 sm:w-2 sm:h-2 xs:w-2 xs:h-2 text-[#076461] transform group-hover:translate-x-0.5 transition-transform duration-300"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative ribbon edges with shimmer animation */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#FCBD01]/30 to-transparent animate-shimmer"></div>
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#FCBD01]/30 to-transparent animate-shimmer"></div>

      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes float-slow {
          0% {
            transform: translateY(0px) translateX(0px);
          }
          25% {
            transform: translateY(-40px) translateX(20px);
          }
          50% {
            transform: translateY(-60px) translateX(0px);
          }
          75% {
            transform: translateY(-40px) translateX(-20px);
          }
          100% {
            transform: translateY(0px) translateX(0px);
          }
        }

        @keyframes float-medium {
          0% {
            transform: translateY(0px) translateX(0px) scale(1);
          }
          33% {
            transform: translateY(-50px) translateX(30px) scale(1.2);
          }
          66% {
            transform: translateY(-30px) translateX(-25px) scale(0.9);
          }
          100% {
            transform: translateY(0px) translateX(0px) scale(1);
          }
        }

        @keyframes float-fast {
          0% {
            transform: translateY(0px) translateX(0px) rotate(0deg);
          }
          25% {
            transform: translateY(-70px) translateX(-30px) rotate(90deg);
          }
          50% {
            transform: translateY(-80px) translateX(40px) rotate(180deg);
          }
          75% {
            transform: translateY(-50px) translateX(-20px) rotate(270deg);
          }
          100% {
            transform: translateY(0px) translateX(0px) rotate(360deg);
          }
        }

        @keyframes bounce-subtle {
          0% {
            transform: translateY(0px) scale(1);
          }
          25% {
            transform: translateY(-35px) scale(1.3);
          }
          50% {
            transform: translateY(-60px) scale(0.8);
          }
          75% {
            transform: translateY(-25px) scale(1.1);
          }
          100% {
            transform: translateY(0px) scale(1);
          }
        }

        @keyframes pulse-gentle {
          0% {
            opacity: 0.4;
            transform: scale(1) translateX(0px);
          }
          25% {
            opacity: 0.8;
            transform: scale(1.5) translateX(15px);
          }
          50% {
            opacity: 1;
            transform: scale(2) translateX(0px);
          }
          75% {
            opacity: 0.8;
            transform: scale(1.5) translateX(-15px);
          }
          100% {
            opacity: 0.4;
            transform: scale(1) translateX(0px);
          }
        }

        @keyframes rotate-slow {
          0% {
            transform: rotate(45deg) translateX(0px) translateY(0px);
          }
          25% {
            transform: rotate(135deg) translateX(25px) translateY(-25px);
          }
          50% {
            transform: rotate(225deg) translateX(0px) translateY(-40px);
          }
          75% {
            transform: rotate(315deg) translateX(-25px) translateY(-25px);
          }
          100% {
            transform: rotate(405deg) translateX(0px) translateY(0px);
          }
        }

        @keyframes rotate-medium {
          0% {
            transform: rotate(45deg) scale(1);
          }
          33% {
            transform: rotate(165deg) scale(1.4);
          }
          66% {
            transform: rotate(285deg) scale(0.7);
          }
          100% {
            transform: rotate(405deg) scale(1);
          }
        }

        @keyframes rotate-reverse {
          0% {
            transform: rotate(45deg) translateY(0px);
          }
          25% {
            transform: rotate(-45deg) translateY(-30px);
          }
          50% {
            transform: rotate(-135deg) translateY(-50px);
          }
          75% {
            transform: rotate(-225deg) translateY(-30px);
          }
          100% {
            transform: rotate(-315deg) translateY(0px);
          }
        }

        @keyframes orbital {
          0% {
            transform: translateX(0px) translateY(0px);
          }
          25% {
            transform: translateX(50px) translateY(-30px);
          }
          50% {
            transform: translateX(0px) translateY(-60px);
          }
          75% {
            transform: translateX(-50px) translateY(-30px);
          }
          100% {
            transform: translateX(0px) translateY(0px);
          }
        }

        @keyframes diagonal-drift {
          0% {
            transform: translateX(0px) translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateX(80px) translateY(-80px) rotate(180deg);
          }
          100% {
            transform: translateX(0px) translateY(0px) rotate(360deg);
          }
        }

        @keyframes wave-motion {
          0% {
            transform: translateX(0px) translateY(0px);
          }
          25% {
            transform: translateX(40px) translateY(-20px);
          }
          50% {
            transform: translateX(80px) translateY(0px);
          }
          75% {
            transform: translateX(40px) translateY(20px);
          }
          100% {
            transform: translateX(0px) translateY(0px);
          }
        }

        @keyframes shimmer {
          0% {
            background-position: -200% center;
          }
          100% {
            background-position: 200% center;
          }
        }

        .animate-spin {
          animation: spin 8s linear infinite;
        }

        .animate-float-slow {
          animation: float-slow 8s ease-in-out infinite;
        }

        .animate-float-medium {
          animation: float-medium 6s ease-in-out infinite;
          animation-delay: 1s;
        }

        .animate-float-fast {
          animation: float-fast 5s ease-in-out infinite;
          animation-delay: 2s;
        }

        .animate-bounce-subtle {
          animation: bounce-subtle 7s ease-in-out infinite;
          animation-delay: 1.5s;
        }

        .animate-pulse-gentle {
          animation: pulse-gentle 4s ease-in-out infinite;
          animation-delay: 2.5s;
        }

        .animate-rotate-slow {
          animation: rotate-slow 12s linear infinite;
        }

        .animate-rotate-medium {
          animation: rotate-medium 8s linear infinite;
        }

        .animate-rotate-reverse {
          animation: rotate-reverse 10s linear infinite;
        }

        .animate-orbital {
          animation: orbital 9s ease-in-out infinite;
          animation-delay: 0.5s;
        }

        .animate-diagonal-drift {
          animation: diagonal-drift 11s ease-in-out infinite;
          animation-delay: 3s;
        }

        .animate-wave-motion {
          animation: wave-motion 6s ease-in-out infinite;
          animation-delay: 1.8s;
        }

        .animate-shimmer {
          background-size: 200% 100%;
          animation: shimmer 3s ease-in-out infinite;
        }

        /* Hide scrollbar */
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        /* Reduce motion for users who prefer it */
        @media (prefers-reduced-motion: reduce) {
          .animate-float-slow,
          .animate-float-medium,
          .animate-float-fast,
          .animate-bounce-subtle,
          .animate-pulse-gentle,
          .animate-rotate-slow,
          .animate-rotate-medium,
          .animate-rotate-reverse,
          .animate-orbital,
          .animate-diagonal-drift,
          .animate-wave-motion,
          .animate-shimmer {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
};

export default Cat;
